'use client';

import { useCallback, useContext, useState, useEffect, type FC } from 'react';
import {
  ConfirmRemoveDocument,
  Button,
  ButtonGroup,
  UploadBox,
  Flex,
  InlineUnsafe,
  Text,
  Pill,
  Tooltip,
  UploadLaterDialog,
  Shimmer,
} from '@/_components';
import { uploadToStorage } from '@/_api/client-documents';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import {
  getRealDocumentPathWithOriginal,
  makeDocument,
} from '@/_utils/documents';
import { deletePath } from '@/_api/integrations';
import { DocumentItem, ExtractedDocumentItem } from '@/_types/models/documents';
import {
  PREDEFINED_MARKED_COMPLETED,
  PREDEFINED_PROVIDED_ELSEWHERE,
  PREDEFINED_NOT_APPLICABLE,
} from '@/_constants/questions';
import type { PreviewDocumentItemProps } from './PreviewDocumentItem.types';
import { UploadedFile } from './components/UploadedFile';
import { UndoMark } from './components/UndoMark';
import { getUniqueFiles, validateFile } from '@/_utils/files';
import { UploadedFileIndicator } from './components/UploadedFileIndicator';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { getUploadedItems } from '@/_utils/answers';
import { getUploadPath } from '@/_utils/questionnaire';
import { removeHtml } from '@/_utils/sanitize';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { getIsPastDate } from '@/_utils/dates';
import { InlineCommentList } from './components/InlineCommentList';
import { DocumentNamingDialog } from './components/DocumentNamingDialog';

export const PreviewDocumentItem: FC<PreviewDocumentItemProps> = ({
  documentsFromAnswer = [],
  droppablePlaceholder,
  isAdditionalAttachments,
  isLoading,
  mode,
  loadQuestionnaire,
  question,
  questionnaire,
  setLoadingItemId,
  itemId,
  uploadedAdditionalAttachments = [],
  loadingSections,
}) => {
  const { preferences } = useContext(FirmSettingsContext);
  const {
    setPreviewFile,
    setPreviewFileOriginalLink,
    setPreviewExtractedDocumentId,
    setPreviewCompanyIdForAnnotations,
  } = useContext(PreviewContext);

  const [isMarkingNotApplicable, setIsMarkingNotApplicable] =
    useState<boolean>(false);
  const [isMarkingProvidedElsewhere, setIsMarkingProvidedElsewhere] =
    useState<boolean>(false);
  const isDisabled: boolean =
    mode === 'admin-template' ||
    (mode === 'client' && (questionnaire?.locked || questionnaire?.completed));
  const [isUploading, setUploading] = useState<boolean>(false);
  const [showFileUpload, setShowFileUpload] = useState<boolean>(false);
  const [isUploadLaterDialogOpen, setIsUploadLaterDialogOpen] = useState(false);
  const { updateQuestion } = useContext(ClientQuestionnaireContext);

  const [
    additionalAttachmentsPendingDrop,
    setAdditionalAttachmentsPendingDrop,
  ] = useState<Array<File>>([]);
  const [
    additionalAttachmentsDescriptionHash,
    setAdditionalAttachmentsDescriptionHash,
  ] = useState<Record<string, string>>({});

  // Dialog state for naming additional attachments
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [isNamingDialogOpen, setIsNamingDialogOpen] = useState<boolean>(false);
  const [currentFileName, setCurrentFileName] = useState<string>('');

  const onToggleFile = () => setShowFileUpload(!showFileUpload);

  const onClearDocumentNameEdit = () => {
    setAdditionalAttachmentsPendingDrop([]);
    setAdditionalAttachmentsDescriptionHash({});
    setCurrentFileIndex(0);
    setIsNamingDialogOpen(false);
    setCurrentFileName('');
  };

  const { addToast } = useContext(ToastContext);
  const { isAdvisor, token } = useContext(UserContext);

  const documents: Array<string | null> = isAdditionalAttachments
    ? uploadedAdditionalAttachments
    : documentsFromAnswer || [];

  // Track blob URLs for cleanup
  const [blobUrlToClean, setBlobUrlToClean] = useState<string | null>(null);

  // Clean up blob URLs when preview closes
  useEffect(() => {
    return () => {
      if (blobUrlToClean) {
        URL.revokeObjectURL(blobUrlToClean);
      }
    };
  }, [blobUrlToClean]);

  const isPastDue: boolean = getIsPastDate(questionnaire?.due_date);

  const onDropOfAdditionalAttachments = useCallback(
    async (acceptedFiles: Array<File>) => {
      const newFiles = getUniqueFiles(
        additionalAttachmentsPendingDrop,
        acceptedFiles,
      );

      setAdditionalAttachmentsPendingDrop(newFiles);
      setShowFileUpload(false);

      // Open dialog to name the first file
      if (newFiles.length > 0) {
        setCurrentFileIndex(0);
        setCurrentFileName(newFiles[0].name);
        setIsNamingDialogOpen(true);
      }
    },
    [additionalAttachmentsPendingDrop],
  );

  const onPreviewDocument = async (path: string) => {
    const { downloadedLink, originalLink } =
      await getRealDocumentPathWithOriginal(questionnaire, path, token!);

    if (
      preferences?.annotation_enabled &&
      questionnaire?.extracted_documents?.length
    ) {
      const foundExtractedDocument: ExtractedDocumentItem | undefined =
        questionnaire?.extracted_documents?.find(
          (doc) => path && doc.source?.includes(path),
        );

      if (foundExtractedDocument?.id) {
        setPreviewCompanyIdForAnnotations(questionnaire?.company_id);
        setPreviewExtractedDocumentId(foundExtractedDocument?.id);
      }
    }

    setPreviewFileOriginalLink(originalLink);
    return setPreviewFile(downloadedLink);
  };

  const onRemoveDocument = async (docPath: any) => {
    try {
      await deletePath({
        companyId: questionnaire.company_id,
        path: '/' + docPath,
        token,
      });

      if (question) {
        let newAnswer = question.answer
          .replace(`&!&${docPath}`, '')
          .replace(docPath, '');

        if (newAnswer === '&!&') newAnswer = '';

        await updateQuestion({
          answer: newAnswer,
          companyId: questionnaire.company_id,
          questionId: question?.id,
        });
      }

      await loadQuestionnaire();
      addToast('success', 'Document removed successfully');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const uploadFiles = useCallback(
    async (acceptedFiles: any, descriptionHash?: Record<string, string>) => {
      if (isAdditionalAttachments) {
        acceptedFiles = additionalAttachmentsPendingDrop;
      }

      // Use provided hash or fall back to state
      const hashToUse = descriptionHash || additionalAttachmentsDescriptionHash;

      setUploading(true);

      let path = `${questionnaire.document_path}`;

      if (
        !questionnaire.document_path ||
        questionnaire.document_path.includes('{year}')
      ) {
        path = getUploadPath(questionnaire);
      }

      if (acceptedFiles.length === 0) {
        setUploading(false);

        return addToast(
          'error',
          'Could not upload those files. Try uploading them one at a time.',
        );
      }

      const newDocuments: Array<DocumentItem> = [];

      for (const file of acceptedFiles) {
        const { isValid } = await validateFile(file);

        if (!isValid) {
          addToast(
            'error',
            'We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.',
          );
          continue;
        }

        // TODO: Fix this. Not working properly
        // if (isProtectedPDF) {
        //   addToast(
        //     'error',
        //     'The PDFs you uploaded are either password-protected or cannot be opened. Please either remove the password or upload alternative files.',
        //   );
        //   continue;
        // }

        if (isAdditionalAttachments) {
          const uploaded: any = await uploadToStorage({
            companyId: questionnaire.company_id,
            file,
            markedAsOthers: true,
            newName: hashToUse[file.name],
            path: `/${path}`,
            questionnaireId: questionnaire.id,
            requiresSplitting: true,
            selectedOptions: [hashToUse[file.name]],
            tags: ['Additional Attachments'],
            token,
          });

          if (uploaded?.error) {
            addToast('error', uploaded.error);

            setUploading(false);
            onClearDocumentNameEdit();
            return;
          }

          if (uploaded?.is_duplicate) {
            addToast('error', `${file?.name} was already uploaded`, {
              error: uploaded,
            });

            setUploading(false);
            onClearDocumentNameEdit();
            return;
          }
        } else {
          const uploaded: any = await uploadToStorage({
            file,
            questionnaireId: questionnaire.id,
            companyId: questionnaire.company_id,
            path: `/${path}`,
            shouldSkipMatching: true,
            token,
          });

          if (uploaded?.error) {
            addToast('error', uploaded.error);

            setUploading(false);
            return;
          }

          if (uploaded?.is_duplicate) {
            addToast('error', `${file?.name} was already uploaded`, {
              error: uploaded,
            });

            setUploading(false);
            return;
          } else {
            const newDocument = makeDocument(uploaded);

            newDocuments.push(newDocument);
          }
        }
      }

      if (!isAdditionalAttachments && question) {
        let newAnswer = question.answer || '';

        newDocuments.forEach((doc) => {
          if (!newAnswer.includes(doc.name)) {
            newAnswer += `&!&${path}/${doc.name}`;
          }
        });

        await updateQuestion({
          answer: newAnswer,
          companyId: questionnaire.company_id,
          questionId: question.id,
        });
      }

      await loadQuestionnaire();

      if (isAdditionalAttachments) {
        // We'll reload the questionnaire after 1 second to give the system time to process the uploaded documents
        // since this seems to take longer for some reason
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await loadQuestionnaire();
        onClearDocumentNameEdit();
      }

      addToast('success', 'Document(s) uploaded successfully');

      setUploading(false);
      setShowFileUpload(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      additionalAttachmentsDescriptionHash,
      additionalAttachmentsPendingDrop,
      addToast,
      isAdditionalAttachments,
      loadQuestionnaire,
      question,
      questionnaire,
      token,
    ],
  );

  const onUpload = useCallback(
    async (acceptedFiles: any) => {
      await uploadFiles(acceptedFiles);
    },
    [uploadFiles],
  );

  const handleFileNameSave = useCallback(async () => {
    const file = additionalAttachmentsPendingDrop[currentFileIndex];
    if (!file) return;

    // Create the updated hash with the current file's name
    const updatedHash = {
      ...additionalAttachmentsDescriptionHash,
      [file.name]: currentFileName || file.name,
    };

    // Save to state
    setAdditionalAttachmentsDescriptionHash(updatedHash);

    // Check if there are more files to name
    if (currentFileIndex < additionalAttachmentsPendingDrop.length - 1) {
      // Move to next file
      const nextIndex = currentFileIndex + 1;
      setCurrentFileIndex(nextIndex);
      setCurrentFileName(additionalAttachmentsPendingDrop[nextIndex].name);
    } else {
      // All files named, pass the updated hash directly to uploadFiles
      try {
        await uploadFiles(additionalAttachmentsPendingDrop, updatedHash);
      } catch (error) {
        addToast('error', null, { error });
      }
    }
  }, [
    additionalAttachmentsPendingDrop,
    currentFileIndex,
    currentFileName,
    uploadFiles,
    additionalAttachmentsDescriptionHash,
    addToast,
  ]);

  const handleDialogCancel = useCallback(() => {
    onClearDocumentNameEdit();
  }, []);

  const handlePreviewCurrentFile = useCallback(() => {
    const file = additionalAttachmentsPendingDrop[currentFileIndex];
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      setBlobUrlToClean(blobUrl);
      setPreviewFile(blobUrl);
    }
  }, [additionalAttachmentsPendingDrop, currentFileIndex, setPreviewFile]);

  const markNotApplicable = async () => {
    try {
      setLoadingItemId(itemId);

      if (question) {
        await updateQuestion({
          answer: 'Not Applicable',
          answerBy: null,
          companyId: questionnaire.company_id,
          questionId: question.id,
        });
      }

      await loadQuestionnaire();
      addToast('success', 'Document marked as not applicable');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoadingItemId(null);
      setIsMarkingNotApplicable(false);
    }
  };

  const onNotApplicable = async () => {
    const answerArray = getUploadedItems(question?.answer);
    const hasUploadedAnswer: boolean = !!answerArray?.length;

    if (hasUploadedAnswer) {
      return setIsMarkingNotApplicable(true);
    }

    return markNotApplicable();
  };

  const onRemoveAnswerBy = async () => {
    try {
      setLoadingItemId(itemId);

      if (question) {
        await updateQuestion({
          answer: question.answer,
          answerBy: null,
          companyId: questionnaire.company_id,
          questionId: question.id,
        });
      }

      await loadQuestionnaire();
      addToast('success', 'Answer by date removed');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoadingItemId(null);
    }
  };

  const onUndo = async () => {
    try {
      setLoadingItemId(itemId);

      if (question) {
        await updateQuestion({
          answer: '',
          answerBy: null,
          companyId: questionnaire.company_id,
          questionId: question.id,
        });
      }

      await loadQuestionnaire();
      addToast('success', 'Undo was successful');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoadingItemId(null);
    }
  };

  const onMarkCompleted = async () => {
    try {
      setLoadingItemId(itemId);

      if (question) {
        await updateQuestion({
          answer: PREDEFINED_MARKED_COMPLETED,
          answerBy: null,
          companyId: questionnaire.company_id,
          questionId: question.id,
        });
      }

      await loadQuestionnaire();
      addToast('success', 'Document marked as completed');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoadingItemId(null);
    }
  };

  const markProvidedElseWhere = async () => {
    try {
      setLoadingItemId(itemId);

      if (question) {
        await updateQuestion({
          answer: PREDEFINED_PROVIDED_ELSEWHERE,
          answerBy: null,
          companyId: questionnaire.company_id,
          questionId: question.id,
        });
      }

      await loadQuestionnaire();
      addToast('success', 'Document marked as provided elsewhere');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoadingItemId(null);
      setIsMarkingProvidedElsewhere(false);
    }
  };

  const onProvidedElsewhere = async () => {
    const answerArray = getUploadedItems(question?.answer);
    const hasUploadedAnswer: boolean = !!answerArray?.length;

    if (hasUploadedAnswer) {
      return setIsMarkingProvidedElsewhere(true);
    }

    return markProvidedElseWhere();
  };

  const isActionable: boolean = ![
    PREDEFINED_PROVIDED_ELSEWHERE,
    PREDEFINED_NOT_APPLICABLE,
  ].includes(question?.answer as any);

  const hasDocumentsNeeded: boolean = !(
    isAdditionalAttachments && additionalAttachmentsPendingDrop.length > 0
  );

  const headerTitle: string | undefined =
    (isAdditionalAttachments && 'Additional Attachments') ||
    question?.options?.join(', ') ||
    question?.text;

  const isComplete: boolean = !!question?.answer;
  const isWaiting: boolean = !!question?.answer_by && !documents.length;

  const isPredefinedAnswer: boolean =
    question?.answer === PREDEFINED_NOT_APPLICABLE ||
    question?.answer === PREDEFINED_PROVIDED_ELSEWHERE ||
    question?.answer === PREDEFINED_MARKED_COMPLETED;

  const helperText = () => {
    if (!question) return null;

    const {
      text,
      options,
      trigger_for_follow_up_question,
      uploaded_last_year,
    } = question;

    const personName = mode === 'client' ? 'You' : 'Client';

    if (uploaded_last_year && text.includes('upload all other')) {
      return (
        <>
          If {mode === 'client' ? 'you have' : 'client has'} any other{' '}
          {options.join(', ')}, please upload them.
        </>
      );
    } else if (uploaded_last_year) {
      return <>{personName} had this last year.</>;
    }

    const textStrippedOfHtml = removeHtml(text);

    if (!textStrippedOfHtml) return null;

    if (trigger_for_follow_up_question) {
      return (
        <>
          {personName} answered{' '}
          <strong className="text-semi-bold">”{textStrippedOfHtml}”</strong> to
          the question{' '}
          <strong className="text-semi-bold">
            ”{trigger_for_follow_up_question}”
          </strong>
          .
        </>
      );
    }

    return (
      <>
        {personName} answered{' '}
        <strong className="text-semi-bold">”{textStrippedOfHtml}”</strong>.
      </>
    );
  };

  const onSplitDocument = async () => {
    await loadQuestionnaire();
  };

  const isSectionStatusLoading: boolean = !!loadingSections?.includes(
    question?.id.toString() || '',
  );

  return (
    <>
      {isUploadLaterDialogOpen && question && (
        <UploadLaterDialog
          hasDocuments={documents && documents.length > 0}
          isOpen={isUploadLaterDialogOpen}
          mode={mode}
          isDisabled={!!isDisabled}
          isUploadLater={!!question?.answer_by}
          onCloseDialog={() => setIsUploadLaterDialogOpen(false)}
          onSuccess={loadQuestionnaire}
          question={question}
          shouldWaitForQuestionToBeAnswered={true}
        />
      )}

      {isNamingDialogOpen &&
        additionalAttachmentsPendingDrop[currentFileIndex] && (
          <DocumentNamingDialog
            isOpen={isNamingDialogOpen}
            currentFileIndex={currentFileIndex}
            totalFiles={additionalAttachmentsPendingDrop.length}
            currentFileName={currentFileName}
            originalFileName={
              additionalAttachmentsPendingDrop[currentFileIndex].name
            }
            isUploading={isUploading}
            onFileNameChange={setCurrentFileName}
            onSave={handleFileNameSave}
            onCancel={handleDialogCancel}
            onPreview={handlePreviewCurrentFile}
          />
        )}

      <Flex isColumn={true} gap={4}>
        <Flex isYCentered={true} gap={4}>
          <UploadedFileIndicator
            isAdditional={isAdditionalAttachments}
            isComplete={isComplete}
            isWaiting={isWaiting}
            isLoading={isSectionStatusLoading}
          />
          <InlineUnsafe text={headerTitle} />
          {question?.optional && <Pill intent="default" label="Optional" />}
        </Flex>

        {!isAdditionalAttachments && <Text type="small">{helperText()}</Text>}
      </Flex>

      {isLoading ? (
        <Shimmer height={16} width={100} isRounded={true} isFullWidth={true} />
      ) : (
        <>
          {!isPredefinedAnswer && (
            <>
              <UploadedFile
                documents={documents}
                uneditable={isDisabled}
                mode={mode}
                isLoading={isLoading}
                onPreviewDocument={onPreviewDocument}
                onRemoveDocument={onRemoveDocument}
                questionnaire={questionnaire}
                shouldAddRecognitionAiPill={
                  mode === 'client' && !isAdditionalAttachments
                }
                onSplitDocument={onSplitDocument}
              />
              {droppablePlaceholder}
            </>
          )}
          {showFileUpload && !isDisabled && !question?.answer && (
            <UploadBox
              onDrop={
                isAdditionalAttachments
                  ? onDropOfAdditionalAttachments
                  : onUpload
              }
              allowMultiple={true}
              isLoading={isUploading}
            />
          )}
          {isMarkingNotApplicable && (
            <ConfirmRemoveDocument
              onConfirm={markNotApplicable}
              onCancel={() => setIsMarkingNotApplicable(false)}
            />
          )}
          {isMarkingProvidedElsewhere && (
            <ConfirmRemoveDocument
              onConfirm={markProvidedElseWhere}
              onCancel={() => setIsMarkingProvidedElsewhere(false)}
            />
          )}
          {question?.answer_by && (
            <UndoMark
              description={`Marked as to be uploaded after ${question?.answer_by}.`}
              onUndo={onRemoveAnswerBy}
              isDisabled={isDisabled}
              value={`Remove Answer By ${question?.answer_by}`}
            />
          )}
          {question?.answer === PREDEFINED_NOT_APPLICABLE && (
            <UndoMark
              description="Marked as not applicable."
              onUndo={onUndo}
              isDisabled={isDisabled}
            />
          )}
          {question?.answer === PREDEFINED_MARKED_COMPLETED && (
            <UndoMark
              description="Marked as completed."
              onUndo={onUndo}
              isDisabled={isDisabled}
            />
          )}
          {question?.answer === PREDEFINED_PROVIDED_ELSEWHERE && (
            <UndoMark
              description="Marked as Provided Elsewhere."
              onUndo={onUndo}
              isDisabled={isDisabled}
            >
              {mode === 'admin-preview' && (
                <InlineCommentList question={question} />
              )}
            </UndoMark>
          )}
          {isActionable && !isPredefinedAnswer && (
            <ButtonGroup isSmallGroup={true}>
              {hasDocumentsNeeded && !isDisabled && (
                <Button
                  intent="primary"
                  isDisabled={isLoading}
                  onClick={onToggleFile}
                  type="button"
                  size="small"
                  value="Upload"
                />
              )}
              {!isAdditionalAttachments && (
                <>
                  {documentsFromAnswer.length === 0 &&
                    mode === 'admin-preview' && (
                      <Button
                        intent="ghost"
                        isDisabled={isLoading || isDisabled}
                        onClick={onMarkCompleted}
                        size="small"
                        type="button"
                        value="Mark Completed"
                      />
                    )}
                  <Button
                    intent="ghost"
                    isDisabled={isLoading || isDisabled}
                    onClick={onNotApplicable}
                    size="small"
                    type="button"
                    value="Not Applicable"
                  />
                  {!preferences?.disable_provided_elsewhere && (
                    <Button
                      intent="ghost"
                      isDisabled={isLoading || isDisabled}
                      onClick={onProvidedElsewhere}
                      size="small"
                      type="button"
                      value="Provided Elsewhere"
                    />
                  )}
                  {!isAdvisor &&
                    !question?.answer_by &&
                    !question?.disable_answer_later && (
                      <Tooltip isDisabled={!isPastDue}>
                        <Tooltip.Content>
                          <Text type="small" color="currentcolor">
                            Questionnaire is past the due date.
                          </Text>
                        </Tooltip.Content>
                        <Tooltip.Trigger>
                          <Button
                            intent="ghost"
                            isDisabled={isLoading || isDisabled || isPastDue}
                            onClick={() => setIsUploadLaterDialogOpen(true)}
                            size="small"
                            type="button"
                            value="Upload Later"
                          />
                        </Tooltip.Trigger>
                      </Tooltip>
                    )}
                </>
              )}
            </ButtonGroup>
          )}
        </>
      )}
    </>
  );
};
